import { DateTime } from "luxon"
import { Box, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/pro-light-svg-icons"
import { add, type Dinero, multiply } from "dinero.js"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../../currency/useMoney"
import { toDinero } from "../../../../../../currency/currencyLib"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import {
    fetchProductsForUser,
    FetchProductsForUserResponse,
    UserDetails,
} from "../../../../../../nonAdminUser/nonAdminUserApi"
import { MeetingServiceOrder } from "../../meetingServiceTypes"
import { ProductionOrderEdit, ProductionProductOption } from "../useProductionOrderEdit"
import { ProductionOrderLines } from "./ProductionOrderLines"

const selectProductOptions = (data: FetchProductsForUserResponse): ProductionProductOption[] =>
    (data.productGroups ?? []).flatMap((group) =>
        (group.products ?? []).map((product) => ({
            productId: product.id,
            name: product.name,
            itemPrice: product.price,
            stock: product.stock,
            availability: product.orderDetails?.availability,
        })),
    )

const useStyles = makeStyles()((theme) => ({
    editContainer: {
        padding: "1rem 1rem 1rem 8.4rem",
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("md")]: {
            paddingLeft: "1rem",
        },
    },
    addButton: {
        "&.MuiButton-root": {
            height: "3rem",
            padding: ".75rem 1rem",
            borderRadius: ".625rem",
            border: `2px dashed ${theme.palette.tailwind.slate[300]}`,
            backgroundColor: "transparent",
            color: theme.palette.tailwind.blue[500],
            fontSize: ".875rem",
            fontWeight: 500,
            justifyContent: "flex-start",
        },
        "&.MuiButton-root:hover": {
            borderColor: theme.palette.tailwind.blue[500],
            backgroundColor: "transparent",
        },
        "& .MuiButton-startIcon svg": {
            fontSize: "1.125rem",
        },
    },
    totalValue: {
        alignSelf: "flex-end",
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
    },
}))

type Props = Readonly<{
    order: MeetingServiceOrder
    edit: ProductionOrderEdit
    onStartEdit: (deliveryKey: string, deliveryTime: DateTime) => void
}>

export function ProductionOrderItemsSection({ order, edit, onStartEdit }: Props) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    // Shared product catalog for every editable line's product picker (React Query dedupes the request).
    const productsQuery = useApiQuery(fetchProductsForUser, {
        queryName: UserDetails.USER_PRODUCTS_LIST,
        dependencies: {
            userId: edit.addLookup?.userId ?? 0,
            merchantId: edit.addLookup?.merchantId ?? 0,
            priceListId: edit.addLookup?.priceListId ?? 0,
            deliveryDate: edit.addLookup?.deliveryDate ?? "",
        },
        enabled: edit.isEditing && Boolean(edit.addLookup),
        select: selectProductOptions,
    })

    const totalAmount = edit.editingLines.reduce<Dinero<number> | undefined>((total, line) => {
        const amount = multiply(toDinero(line.itemPrice), line.count)
        return total ? add(total, amount) : amount
    }, undefined)
    const totalLabel = totalAmount ? moneyFactory.format(totalAmount) : ""

    const editFooter = (
        <Stack spacing=".75rem" className={classes.editContainer}>
            <LocalizedStrict id="meeting-service-production-view-add-menu-button">
                <StandardButton
                    fullWidth
                    size="small"
                    variant="outlined"
                    className={classes.addButton}
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    disabled={!edit.addLookup}
                    onClick={() => edit.addBlankLine()}
                >
                    Add Product
                </StandardButton>
            </LocalizedStrict>

            {totalAmount && (
                <LocalizedStrict id="meeting-service-production-view-total-with-amount" vars={{ amount: totalLabel }}>
                    <Typography variant="body1" className={classes.totalValue}>
                        Total: {totalLabel}
                    </Typography>
                </LocalizedStrict>
            )}
        </Stack>
    )

    return (
        <Box>
            <ProductionOrderLines
                order={order}
                groups={edit.deliveryGroups}
                meetingStart={order.meetingDetails?.startDateTime}
                meetingEnd={order.meetingDetails?.endDateTime}
                editingKey={edit.editingKey}
                isSaving={edit.isSaving}
                hasIncompleteLine={edit.hasIncompleteLine}
                saveAttempted={edit.saveAttempted}
                touchedKeys={edit.touchedKeys}
                usedProductIds={edit.usedProductIds}
                onCountChange={edit.setLineCount}
                onRemove={edit.removeLine}
                onStartEdit={onStartEdit}
                onSave={edit.save}
                onCancel={edit.cancelEdit}
                productOptions={productsQuery.data ?? []}
                isProductsLoading={productsQuery.isInitialLoading}
                isEditLoading={edit.isEditLoading}
                onProductChange={edit.setLineProduct}
                editFooter={editFooter}
            />
        </Box>
    )
}
